// Single source of truth for the pop-counter wire protocol.
// Imported by BOTH the browser client (src/net) and the Worker (worker/) so
// the two sides can never drift.

export type ClientMsg = { t: "p"; n: number };

export type ServerMsg =
  /**
   * `c` is the country the server attributes THIS connection's pops to
   * (ISO 3166-1 alpha-2, "XX" for unattributable) — sent once so the client
   * can say "your pops land in KR's column" without a separate endpoint.
   */
  | { t: "init"; total: number; live: number; c?: string }
  /**
   * `d` is who popped since the last broadcast: country code → pop count,
   * capped at MAX_DELTA_COUNTRIES entries. Nothing is stored — it exists only
   * in flight, so a stranger's pop can sparkle on everyone's screen. Absent
   * when the window was quiet, and absent from the HTTP fallback on purpose:
   * seeing the world pop is the live connection's privilege.
   */
  | { t: "sync"; total: number; live: number; d?: Record<string, number> }
  /**
   * The Grand Tally just crossed `threshold`. Sent the instant it happens,
   * outside the sync coalescing window, so every connected visitor sees the
   * same moment together. Deliberately NOT replayed to clients that connect
   * afterwards — a milestone is a live event; latecomers read it off the
   * Observatory's timeline instead.
   */
  | { t: "m"; threshold: number; total: number }
  | { t: "slow" };

export const MAX_BATCH = 50;
export const MAX_MSG_BYTES = 64;
/** Client coalesces pops and sends at most one message per this interval. */
export const CLIENT_BATCH_MS = 500;
/** Server coalesces outgoing sync broadcasts to this interval. */
export const BROADCAST_MS = 250;
/** Server rejects messages arriving faster than this per connection. */
export const MIN_MSG_INTERVAL_MS = 200;
/** A sync's country-delta map ships at most this many countries (busiest win). */
export const MAX_DELTA_COUNTRIES = 6;

/** The `d` map of a sync, validated entry by entry; null when nothing survives. */
function parseDeltas(raw: unknown): Record<string, number> | null {
  if (typeof raw !== "object" || raw === null) return null;
  const out: Record<string, number> = {};
  let kept = 0;
  for (const [k, v] of Object.entries(raw)) {
    if (kept >= MAX_DELTA_COUNTRIES) break;
    if (!/^[A-Z]{2}$/.test(k)) continue;
    if (typeof v !== "number" || !Number.isFinite(v) || v < 1) continue;
    out[k] = Math.floor(v);
    kept++;
  }
  return kept > 0 ? out : null;
}

export function encodePops(n: number): string {
  const clamped = Math.max(1, Math.min(MAX_BATCH, Math.floor(n)));
  return JSON.stringify({ t: "p", n: clamped });
}

export function parseClientMsg(raw: string): ClientMsg | null {
  if (raw.length > MAX_MSG_BYTES) return null;
  try {
    const v: unknown = JSON.parse(raw);
    if (
      typeof v === "object" &&
      v !== null &&
      (v as { t?: unknown }).t === "p" &&
      typeof (v as { n?: unknown }).n === "number" &&
      Number.isFinite((v as { n: number }).n)
    ) {
      const n = Math.floor((v as { n: number }).n);
      if (n >= 1 && n <= MAX_BATCH) return { t: "p", n };
    }
  } catch {
    /* malformed JSON → null */
  }
  return null;
}

export function parseServerMsg(raw: string): ServerMsg | null {
  try {
    const v: unknown = JSON.parse(raw);
    if (typeof v !== "object" || v === null) return null;
    const t = (v as { t?: unknown }).t;
    if (t === "slow") return { t: "slow" };
    if (t === "m") {
      const threshold = (v as { threshold?: unknown }).threshold;
      const total = (v as { total?: unknown }).total;
      if (
        typeof threshold === "number" &&
        Number.isFinite(threshold) &&
        threshold > 0 &&
        typeof total === "number" &&
        Number.isFinite(total) &&
        total >= 0
      ) {
        return { t: "m", threshold: Math.floor(threshold), total: Math.floor(total) };
      }
      return null;
    }
    if (t === "init" || t === "sync") {
      const total = (v as { total?: unknown }).total;
      const live = (v as { live?: unknown }).live;
      if (
        typeof total === "number" &&
        Number.isFinite(total) &&
        total >= 0 &&
        typeof live === "number" &&
        Number.isFinite(live) &&
        live >= 0
      ) {
        if (t === "sync") {
          const d = parseDeltas((v as { d?: unknown }).d);
          return d
            ? { t: "sync", total: Math.floor(total), live: Math.floor(live), d }
            : { t: "sync", total: Math.floor(total), live: Math.floor(live) };
        }
        const c = (v as { c?: unknown }).c;
        return typeof c === "string" && /^[A-Z]{2}$/.test(c)
          ? { t: "init", total: Math.floor(total), live: Math.floor(live), c }
          : { t: "init", total: Math.floor(total), live: Math.floor(live) };
      }
    }
  } catch {
    /* malformed JSON → null */
  }
  return null;
}
